import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Lock, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { gbp, ukDate } from "../lib/format";
import { Loading } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const fmtLocked = (k, v) => (v == null ? "—" : k === "signed_date" ? ukDate(v) : gbp(v));

function Cell({ row, col, canEdit, onSave, onPaste }) {
  const v = row.values[col.key];
  const [val, setVal] = useState(v ?? "");
  const [err, setErr] = useState("");
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setVal(v ?? ""); }, [v]);
  const missing = row.missing.includes(col.key);
  const id = `cell-${row.id}-${col.key}`;
  if (!canEdit) return <td className={`border-b border-r px-2 py-1.5 text-xs ${missing ? "bg-primary/10" : ""}`} data-testid={id}>{String(v ?? "")}</td>;
  const blur = async () => {
    focused.current = false;
    if (String(val ?? "") === String(v ?? "")) return setErr("");
    const e = await onSave(row.id, col.key, val);
    setErr(e || "");
  };
  return (
    <td className={`relative border-b border-r p-0 ${err ? "bg-primary/20 outline outline-2 -outline-offset-2 outline-primary" : missing ? "bg-primary/10" : ""}`} title={err || ""}>
      <input className="h-8 w-full min-w-[9rem] bg-transparent px-2 text-xs focus:bg-background focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
        value={val} onChange={(e) => setVal(e.target.value)} onFocus={() => { focused.current = true; }} onBlur={blur}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        onPaste={(e) => { const t = e.clipboardData.getData("text"); if (/[\t\n]/.test(t.trim())) { e.preventDefault(); onPaste(row.id, col.key, t); } }}
        placeholder={missing ? "missing" : ""} data-testid={id} />
      {err && <div className="px-2 pb-1 text-[10px] font-semibold leading-tight text-primary" data-testid={`${id}-error`}>{err}</div>}
    </td>
  );
}

function Filters({ f, setF }) {
  const { data: o } = useApi("/insight/filters", { live: false });
  const sel = (k, label, opts) => (
    <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value || undefined })} data-testid={`gaps-filter-${k}`}>
      <option value="">{label}: all</option>{opts.map((x) => (Array.isArray(x) ? <option key={x[0]} value={x[0]}>{x[1]}</option> : <option key={x} value={x}>{x}</option>))}
    </select>);
  if (!o) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="gaps-filters">
      {sel("partner_id", "Partner", o.partners)}{sel("pot", "Pot", ["AM", "AMSM"])}{sel("status", "Status", o.statuses)}
      <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.only_gaps} onChange={(e) => setF({ ...f, only_gaps: e.target.checked || undefined })} data-testid="gaps-filter-only" />Only rows with gaps</label>
      <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.sort || ""} onChange={(e) => setF({ ...f, sort: e.target.value || undefined })} data-testid="gaps-sort">
        <option value="">Sort: register order</option><option value="asc">Least complete first</option><option value="desc">Most complete first</option>
      </select>
    </div>
  );
}

function PreviewDialog({ items, onClose, onConfirm, busy }) {
  if (!items) return null;
  const ok = items.filter((i) => !i.error && !i.unchanged);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="gaps-preview-dialog">
        <DialogHeader><DialogTitle>Preview changes – {ok.length} to save{items.length - ok.length ? `, ${items.filter((i) => i.error).length} refused` : ""}</DialogTitle></DialogHeader>
        <div className="max-h-[55vh] overflow-auto">
          <table className="w-full text-xs"><thead><tr className="border-b text-left text-muted-foreground"><th className="py-1.5">Project</th><th>Field</th><th>Now</th><th>New</th><th /></tr></thead>
            <tbody>{items.map((i, n) => <tr key={n} className={`border-b ${i.error ? "bg-primary/10" : ""}`} data-testid={`gaps-preview-row-${n}`}>
              <td className="py-1.5 pr-2">{i.project_name}</td><td className="pr-2">{i.label}</td><td className="pr-2 text-muted-foreground">{String(i.old ?? "—")}</td>
              <td className="pr-2 font-semibold">{String(i.new ?? "—")}</td><td className="font-semibold text-primary">{i.error || (i.unchanged ? "no change" : "")}</td></tr>)}</tbody></table>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} data-testid="gaps-preview-cancel">Cancel</Button>
          <Button disabled={!ok.length || busy} onClick={() => onConfirm(ok)} data-testid="gaps-preview-confirm">Save {ok.length} change{ok.length === 1 ? "" : "s"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestDialog({ s, onClose, onAccept }) {
  if (!s) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="gaps-suggest-dialog">
        <DialogHeader><DialogTitle>Suggestions for {s.name}</DialogTitle></DialogHeader>
        {s.loading ? <Loading /> : <>
          <p className="text-xs text-muted-foreground">Read from: {s.documents.join(", ") || "—"}. Only empty cells are suggested.</p>
          {!s.suggestions.length && <p className="text-sm" data-testid="gaps-suggest-none">No values found for the empty cells.</p>}
          <div className="max-h-[55vh] space-y-2 overflow-auto">{s.suggestions.map((x) => <div key={x.key} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`suggest-${x.key}`}>
            <div><div className="text-xs text-muted-foreground">{x.label}</div><div className="font-semibold">{String(x.value)}</div></div>
            {x.done ? <span className="text-xs font-semibold">{x.done}</span> : <div className="flex gap-1">
              <Button size="sm" onClick={() => onAccept(x)} data-testid={`suggest-accept-${x.key}`}>Accept</Button>
              <Button size="sm" variant="ghost" onClick={() => onAccept(x, true)} data-testid={`suggest-reject-${x.key}`}>Reject</Button></div>}
          </div>)}</div></>}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="gaps-suggest-close">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FillGaps() {
  const [f, setF] = useState({});
  const params = Object.fromEntries(Object.entries(f).filter(([k, v]) => v && k !== "sort"));
  const { data, setData, error, status } = useApi("/gaps", { params });
  const [preview, setPreview] = useState(null);
  const [sug, setSug] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  if (status === 403) return <div className="rounded-lg border p-6 text-sm" data-testid="gaps-forbidden">{error}</div>;
  if (!data) return <Loading />;
  const cols = data.columns;
  const rows = f.sort ? [...data.rows].sort((a, b) => (f.sort === "asc" ? 1 : -1) * (a.completeness - b.completeness)) : data.rows;
  const patchRow = (row, summary) => setData((d) => ({ ...d, summary, rows: d.rows.map((r) => (r.id === row.id ? row : r)) }));
  const save = async (pid, key, value) => {
    try { const r = await api.put("/gaps/cell", { project_id: pid, key, value }); patchRow(r.data.row, r.data.summary); toast.success("Saved"); return ""; } catch (e) { return errMsg(e); }
  };
  const paste = async (pid, key, text) => {
    const grid = text.replace(/\r/g, "").replace(/\n$/, "").split("\n").map((l) => l.split("\t"));
    const r0 = rows.findIndex((r) => r.id === pid), c0 = cols.findIndex((c) => c.key === key);
    const changes = [];
    grid.forEach((line, i) => line.forEach((v, j) => { const r = rows[r0 + i], c = cols[c0 + j]; if (r && c) changes.push({ project_id: r.id, key: c.key, value: v }); }));
    try { setPreview((await api.post("/gaps/preview", { changes })).data.items); } catch (e) { toast.error(errMsg(e)); }
  };
  const confirm = async (items) => {
    setBusy(true);
    try {
      const r = await api.post("/gaps/apply", { changes: items.map((i) => ({ project_id: i.project_id, key: i.key, value: i.new })) });
      toast.success(`${r.data.saved} cell${r.data.saved === 1 ? "" : "s"} saved`); setPreview(null);
      const fresh = await api.get("/gaps", { params }); setData(fresh.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const suggest = async (r) => {
    setSug({ name: r.name, id: r.id, loading: true });
    try { const d = (await api.post(`/gaps/${r.id}/suggest`)).data; setSug({ name: r.name, id: r.id, ...d }); } catch (e) { setSug(null); toast.error(errMsg(e)); }
  };
  const accept = async (x, reject) => {
    const e = reject ? "" : await save(sug.id, x.key, x.value);
    if (e) return toast.error(e);
    setSug((s) => ({ ...s, suggestions: s.suggestions.map((y) => (y.key === x.key ? { ...y, done: reject ? "Rejected" : "Accepted" } : y)) }));
  };
  const download = async () => {
    const { data: b } = await api.get("/gaps/template", { params, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "fill-the-gaps.xlsx"; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const items = (await api.post("/gaps/template/preview", fd)).data.items; items.length ? setPreview(items) : toast("No changes found in the file"); } catch (e) { toast.error(errMsg(e)); }
    fileRef.current.value = "";
  };
  return (
    <div className="space-y-4" data-testid="gaps-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight">Fill the gaps</h1>
          <p className="text-sm text-muted-foreground">Overall <b data-testid="gaps-completeness">{data.completeness}%</b> complete · <Link to="/insight" className="font-semibold text-primary" data-testid="gaps-exception-count">{data.summary.data_gap_projects} Live projects</Link> with data-gap exceptions (<span data-testid="gaps-exception-fields">{data.summary.data_gap_fields}</span> missing fields){!data.can_edit && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold" data-testid="gaps-readonly">Read-only</span>}</p></div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={download} data-testid="gaps-template-download"><Download className="mr-1 h-4 w-4" />Download template</Button>
          {data.can_edit && <><Button size="sm" variant="outline" onClick={() => fileRef.current.click()} data-testid="gaps-template-upload"><Upload className="mr-1 h-4 w-4" />Upload template</Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="gaps-template-file" /></>}
        </div>
      </div>
      <Filters f={f} setF={setF} />
      {data.can_edit && <p className="text-xs text-muted-foreground">Edit a cell and tab away to save. Paste a block copied from Excel into any cell to fill across and down (you'll see a preview). Highlighted cells are missing. Locked columns come from the proforma.</p>}
      <div className="max-h-[70vh] overflow-auto rounded-lg border bg-card" data-testid="gaps-table-wrap">
        <table className="dt-lite border-separate border-spacing-0 text-xs" data-testid="gaps-table">
          <thead className="sticky top-0 z-20 bg-card"><tr className="text-left text-muted-foreground">
            <th className="sticky left-0 z-30 min-w-[10rem] border-b border-r bg-card px-2 py-2 sm:min-w-[16rem]">Project</th><th className="border-b border-r px-2">Complete</th>
            {cols.map((c) => <th key={c.key} className="whitespace-nowrap border-b border-r px-2 py-2">{c.label}</th>)}
            {data.locked_columns.map((c) => <th key={c.key} className="whitespace-nowrap border-b border-r bg-muted px-2"><Lock className="mr-1 inline h-3 w-3" />{c.label}</th>)}
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} data-testid={`gaps-row-${r.id}`}>
              <td className="sticky left-0 z-10 border-b border-r bg-card px-2 py-1.5">
                <div className="flex items-center justify-between gap-2"><Link to={`/projects/${r.id}#register`} className="line-clamp-2 font-semibold hover:text-primary" data-testid={`gaps-project-${r.id}`}>{r.name}</Link>
                  {data.can_edit && r.missing.length > 0 && <button className="shrink-0 rounded p-1 text-primary hover:bg-muted" title="Suggest from documents" onClick={() => suggest(r)} data-testid={`gaps-suggest-${r.id}`}><Sparkles className="h-3.5 w-3.5" /></button>}</div>
                <div className="text-[10px] text-muted-foreground">{r.partner_name} · {r.status}</div></td>
              <td className="border-b border-r px-2 text-center font-figure font-bold" data-testid={`gaps-pct-${r.id}`}>{r.completeness}%</td>
              {cols.map((c) => <Cell key={c.key} row={r} col={c} canEdit={data.can_edit} onSave={save} onPaste={paste} />)}
              {data.locked_columns.map((c) => <td key={c.key} className="whitespace-nowrap border-b border-r bg-muted/60 px-2 font-figure text-muted-foreground" data-testid={`locked-${r.id}-${c.key}`}>{fmtLocked(c.key, r.locked[c.key])}</td>)}
            </tr>))}</tbody>
        </table>
        {!rows.length && <p className="p-6 text-sm" data-testid="gaps-empty">No projects match.</p>}
      </div>
      <PreviewDialog items={preview} onClose={() => setPreview(null)} onConfirm={confirm} busy={busy} />
      <SuggestDialog s={sug} onClose={() => setSug(null)} onAccept={accept} />
    </div>
  );
}
